import apiClient from "./axiosConfig";

export interface RoleSummary {
  _id: string;
  name: string;
}

export interface UserSummary {
  _id: string;
  name: string;
  userName?: string;
  email: string;
  phoneNumber?: string | null;
  role?: RoleSummary;
  isActive: boolean;
}

export type UserListResponse = UserSummary[];

export const fetchUsers = async (): Promise<UserListResponse> => {
  const res = await apiClient.get<UserListResponse>("/api/rbac/users");
  return res.data;
};

export const updateUserStatus = async (userId: string, isActive: boolean): Promise<UserSummary> => {
  const res = await apiClient.put<UserSummary>(`/api/users/${userId}/status`, {
    isActive,
  });
  return res.data;
};

export const adminResetPassword = async (userId: string, newPassword: string) => {
  const res = await apiClient.post<{ success: boolean }>("/api/users/admin-reset-password", {
    userId,
    newPassword,
  });
  return res.data;
};

export const deleteUser = async (userId: string) => {
  const res = await apiClient.delete<{ deleted: boolean }>(`/api/rbac/users/${userId}`);
  return res.data;
};

// ---------------------------------------------------------------------------------------------
// Users page (/hr/users): typed wrappers over the RBAC user endpoints in routes/rbac.js.

/** A user's role as the list returns it. Legacy users carry only `name`, without an id. */
export interface UserRoleRef {
  _id?: string;
  name?: string;
}

/**
 * One row of GET /api/rbac/users, which merges RBAC users and legacy users. Legacy rows can carry
 * `id` instead of `_id` and their role in `roles` instead of `role`.
 */
export interface ManagedUser {
  _id: string;
  id?: string;
  name: string;
  userName?: string;
  email: string;
  phoneNumber?: string | null;
  role?: UserRoleRef | null;
  roles?: UserRoleRef[];
  isActive: boolean;
  createdAt?: string;
}

/** React Query keys; `all` is shared with the Roles page, which counts people per role. */
export const userKeys = {
  all: ["rbac", "users"] as const,
  logins: (userId: string) => ["rbac", "users", userId, "logins"] as const,
};

const RBAC_USERS = "/api/rbac/users";

const userPath = (userId: string) => `${RBAC_USERS}/${encodeURIComponent(userId)}`;

export const fetchManagedUsers = async (): Promise<ManagedUser[]> => {
  const res = await apiClient.get<ManagedUser[]>(RBAC_USERS);
  return Array.isArray(res.data) ? res.data : [];
};

export interface CreateUserInput {
  name: string;
  userName: string;
  email: string;
  phoneNumber: string;
  password: string;
  /** Role id. */
  role: string;
}

/** Needs userManagement write on the route and approve in the service. */
export const createUser = async (input: CreateUserInput): Promise<ManagedUser> => {
  const res = await apiClient.post<ManagedUser>(RBAC_USERS, input);
  return res.data;
};

export interface UpdateUserInput {
  name?: string;
  userName?: string;
  email?: string;
  phoneNumber?: string;
  /** Role id. */
  role?: string;
  isActive?: boolean;
  /** Only with userManagement configure. */
  password?: string;
}

export const updateUser = async (userId: string, input: UpdateUserInput): Promise<ManagedUser> => {
  const res = await apiClient.put<ManagedUser>(userPath(userId), input);
  return res.data;
};

/** Suspends (`false`) or re-activates (`true`) an account; needs userManagement approve. */
export const setUserActive = async (userId: string, isActive: boolean): Promise<ManagedUser> => {
  const res = await apiClient.patch<ManagedUser>(`${userPath(userId)}/suspend`, { isActive });
  return res.data;
};

/** Sets a new password through PUT /api/rbac/users/:id; needs userManagement configure. */
export const resetUserPassword = (userId: string, password: string): Promise<ManagedUser> =>
  updateUser(userId, { password });

export type LoginLocation =
  | string
  | {
      city?: string | null;
      region?: string | null;
      country?: string | null;
      label?: string | null;
      lat?: number | null;
      lng?: number | null;
    }
  | null;

export interface LoginHistoryEntry {
  timestamp: string;
  ip?: string | null;
  location?: LoginLocation;
  username?: string | null;
  user?: { _id?: string; name?: string | null; userName?: string | null; email?: string | null };
  status?: string | null;
  success?: boolean | null;
}

export const fetchUserLoginHistory = async (userId: string): Promise<LoginHistoryEntry[]> => {
  const res = await apiClient.get<LoginHistoryEntry[]>(`${userPath(userId)}/logins`);
  return Array.isArray(res.data) ? res.data : [];
};
